import React, { useEffect, useRef, useState } from 'react';
import { UPDATE_SCHEDULE } from '~/services/calendar';
import { useMutation } from '@apollo/client';
import moment from 'moment';
import { publicClient } from '~/services';
import { useTranslation } from 'react-i18next';
import { FaCalendarAlt, FaClock, FaTimes } from 'react-icons/fa';
import { format } from 'date-fns';
import { es, enUS } from 'date-fns/locale';

interface AppointmentDialogProps {
  onClose: () => void;
  selectedDate: Date | null;
  selectedTime: { realTime: string; utcTime: string };
  schedule_id: string;
  utc: number;
  token: string;
  calendarDayId: string;
  type: number;
  removeTime: (realTime: string) => void; // Add this prop
  onScheduleUpdated: (updatedSchedule: any) => void; // Modify this prop
}

const AppointmentDialog: React.FC<AppointmentDialogProps> = ({
  onClose,
  selectedDate,
  selectedTime,
  schedule_id,
  utc,
  token,
  calendarDayId,
  type,
  removeTime,
  onScheduleUpdated,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(true);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [updateSchedule] = useMutation(UPDATE_SCHEDULE);
  const [t] = useTranslation('calendar');
  const [isLoading, setIsLoading] = useState(false);

  // Función para mostrar alertas usando DaisyUI toast
  const showAlert = (message: string, type: 'success' | 'error' | 'warning' = 'error') => {
    const toast = document.createElement('div');
    toast.className = 'toast toast-top toast-center';
    toast.innerHTML = `
      <div class="alert alert-brand-${type}">
        <span>${message}</span>
      </div>
    `;
    document.body.appendChild(toast);
    setTimeout(() => {
      if (document.body.contains(toast)) {
        document.body.removeChild(toast);
      }
    }, 4000);
  };

  useEffect(() => {
    if (isModalOpen) {
      dialogRef.current?.showModal();
    } else {
      dialogRef.current?.close();
    }
  }, [isModalOpen]);

  const handleCloseModal = () => {
    if (!isLoading) {
      setIsModalOpen(false);
      onClose();
    }
  };

  const handleSaveSchedule = () => {
    setIsLoading(true);
    showAlert(t('loading'), 'warning');

    const formattedDateTime = moment(selectedDate)
      .set({
        hour: parseInt(selectedTime.realTime.split(':')[0]),
        minute: parseInt(selectedTime.realTime.split(':')[1]),
      })
      .utcOffset(utc, true) // Mantiene la hora local
      .format('YYYY-MM-DDTHH:mm:ssZ');

    updateSchedule({
      variables: {
        data: {
          schedule_id,
          calendarDayId,
          schedule_date: formattedDateTime,
          type,
          userUtc: utc,
        },
      },
      client: publicClient,
      context: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
      onError: (error) => {
        showAlert(t('alert_update_error'), 'error');
      },
    })
      .then((result) => {
        if (result?.data?.update_scheduler.hasError) {
          showAlert(t('alert_schedule_conflict'), 'warning');
        } else {
          const locale = navigator.language.startsWith('es') ? es : enUS;
          showAlert(
            t('alert_schedule_success', {
              date: selectedDate ? format(selectedDate, 'EEEE, MMMM d, yyyy', { locale }) : '',
              time: moment(selectedTime.utcTime, 'HH:mm').format('hh:mm A'), // Convert to 12-hour format
            }),
            'success'
          );
          onScheduleUpdated({
            ...result.data.update_scheduler.schedule,
            start_date: {
              date: formattedDateTime,
              hour: parseInt(selectedTime.realTime.split(':')[0]),
              minute: parseInt(selectedTime.realTime.split(':')[1]),
            },
          });
        }
      })
      .catch((error) => {
        showAlert(t('alert_update_error'), 'error');
      })
      .finally(() => {
        setIsLoading(false);
        removeTime(selectedTime.realTime);
        setIsModalOpen(false);
        onClose();
      });
  };

  return (
    <div>
      <dialog
        ref={dialogRef}
        onClose={handleCloseModal}
        className="modal"
        onClick={handleCloseModal}
      >
        <div
          className="modal-box card-brand max-w-lg"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Encabezado con ícono y botón de cierre */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-brand-heading text-2xl flex items-center gap-3">
              <FaCalendarAlt className="text-brand-primary" />
              {t('appointment_confirm')}
            </h2>
            <button
              onClick={handleCloseModal}
              className="btn btn-ghost btn-sm btn-circle"
              disabled={isLoading}
            >
              <FaTimes size={16} />
            </button>
          </div>

          {/* Información de la cita */}
          <div className="space-y-4 mb-6">
            <div className="flex items-center gap-3 p-4 bg-base-200 rounded-lg">
              <FaCalendarAlt className="text-brand-primary" />
              <div>
                <p className="text-brand-caption font-medium">{t('date_label')}</p>
                <p className="text-brand-body font-semibold">
                  {selectedDate ? format(selectedDate, 'EEEE, MMMM d, yyyy', { locale: navigator.language.startsWith('es') ? es : enUS }) : ''}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-base-200 rounded-lg">
              <FaClock className="text-brand-primary" />
              <div>
                <p className="text-brand-caption font-medium">{t('time_label')}</p>
                <p className="text-brand-body font-semibold">
                  {moment(selectedTime.utcTime, 'HH:mm').format('hh:mm A')}
                </p>
              </div>
            </div>
          </div>

          {/* Botones de acción */}
          <div className="flex justify-end gap-3">
            <button
              onClick={handleCloseModal}
              className="btn btn-ghost"
              disabled={isLoading}
            >
              {t('cancel_button')}
            </button>
            <button
              onClick={handleSaveSchedule}
              className="btn btn-primary rounded-full"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="loading loading-spinner loading-sm"></span>
                  {t('loading')}
                </>
              ) : (
                <>
                  <FaCalendarAlt />
                  {t('confirm_button')}
                </>
              )}
            </button>
          </div>
        </div>
      </dialog>
    </div>
  );
};

export default AppointmentDialog;
